import React, { useState } from "react";
import { Landmark, ShieldQuestion, ChevronDown } from "lucide-react";
import * as api from "../../api";
import { ForensicReport } from "../game/ForensicReport";
import { reviewReasonLabel, isUnregisteredBank } from "../../lib/reviewReasons";

/* FASE 5 — Panel forense del pedido para el admin.
 * Sólo visible en pedidos NEEDS_REVIEW / REJECTED / Rechazado.
 * El desglose forense vive en `verificationLog`, que el listado con
 * `light=1` no envía: se carga en diferido al abrir el acordeón. */
export function OrderForensicPanel({ order }) {
  const [fullOrder, setFullOrder] = useState(
    order?.verificationLog ? order : null
  );
  const [loading, setLoading] = useState(false);

  const visible =
    order?.verification_status === "NEEDS_REVIEW" ||
    order?.verification_status === "REJECTED" ||
    order?.status === "Rechazado";
  if (!visible) return null;

  const loadFull = async () => {
    if (fullOrder || loading) return;
    setLoading(true);
    try {
      setFullOrder(await api.getOrder(order.id));
    } catch (e) {
      console.error("Forensic panel: no se pudo cargar el pedido completo:", e);
    } finally {
      setLoading(false);
    }
  };

  const reason = reviewReasonLabel(order.review_reason);
  const bankName = order.detected_bank_name;
  const bankCode = order.detected_bank_code;
  const registered = order.bank_registered;
  const score = order.confidence_score;

  return (
    <div
      data-testid={`admin-order-forensic-panel-${order.id}`}
      className="p-3 rounded-xl bg-zinc-950/70 border border-purple-500/30 space-y-3"
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[10px] font-mono uppercase tracking-widest text-purple-300">
          Verificación IA
        </span>
        {reason && (
          <span
            data-testid={`admin-order-review-reason-${order.id}`}
            className="px-2 py-0.5 rounded-full bg-amber-500/15 border border-amber-400/40 text-amber-200 text-[10px] font-bold"
          >
            {reason}
          </span>
        )}
        {typeof score === "number" && (
          <span
            data-testid={`admin-order-confidence-${order.id}`}
            className="px-2 py-0.5 rounded-full bg-cyan-500/15 border border-cyan-400/40 text-cyan-200 text-[10px] font-mono font-bold"
          >
            Score {Math.round(score)}%
          </span>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className="inline-flex items-center gap-1.5 text-zinc-400 font-mono text-[11px]">
          <Landmark className="w-3.5 h-3.5 text-zinc-500" />
          Banco detectado:
        </span>
        <span
          data-testid={`admin-order-detected-bank-${order.id}`}
          className="font-bold text-white"
        >
          {bankName || "No detectado"}
          {bankCode ? <span className="font-mono text-zinc-400"> ({bankCode})</span> : null}
        </span>
        {registered === false && (
          <span
            data-testid={`admin-order-bank-unregistered-${order.id}`}
            title="El banco del comprobante no está en el catálogo. NO significa fraude: valida la referencia manualmente."
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-fuchsia-500/15 border border-fuchsia-400/50 text-fuchsia-200 text-[10px] font-bold uppercase tracking-widest"
          >
            <ShieldQuestion className="w-3 h-3" />
            No registrado
          </span>
        )}
      </div>

      {isUnregisteredBank(order.review_reason) && (
        <p className="text-[11px] text-fuchsia-200/80 leading-relaxed">
          El banco emisor no está en el catálogo AG7. El comprobante NO fue
          descartado: revisa la imagen y confirma la referencia con
          &quot;Corrección Manual&quot;.
        </p>
      )}

      <details data-testid="order-forensic-panel-details-1" className="group" onToggle={loadFull}>
        <summary
          data-testid={`forensic-toggle-${order.id}`}
          className="cursor-pointer list-none inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-purple-300 hover:text-purple-200"
        >
          <ChevronDown className="w-3.5 h-3.5 transition-transform group-open:rotate-180" />
          Análisis forense detallado
        </summary>
        <div className="mt-2">
          {loading && (
            <p className="text-[11px] font-mono text-zinc-500" data-testid={`forensic-loading-${order.id}`}>
              Cargando análisis…
            </p>
          )}
          {!loading && fullOrder && <ForensicReport order={fullOrder} />}
          {!loading && fullOrder && !fullOrder?.verificationLog?.extracted?.forensic && (
            <p className="text-[11px] font-mono text-zinc-500" data-testid={`forensic-empty-${order.id}`}>
              Sin datos forenses para este comprobante.
            </p>
          )}
        </div>
      </details>
    </div>
  );
}
